"use client";

import "@vidstack/react/player/styles/base.css";

import type { DASHSrc, HLSSrc } from "@vidstack/react";
import {
  MediaPlayer,
  type MediaPlayerInstance,
  MediaProvider,
  Time,
  TimeSlider,
  VolumeSlider,
} from "@vidstack/react";
import Link from "next/link";
import {
  type ComponentProps,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  LuLayers,
  LuLink2,
  LuVolume,
  LuVolume1,
  LuVolume2,
  LuVolumeX,
} from "react-icons/lu";

import { AddToCollectionDropdown } from "@/components/shared/add-to-collection-dropdown";
import AudioBlobVisualizer from "@/components/shared/media-player/audio-blob-visualizer";
import { LocalMediaStorageWithoutVolume } from "@/components/shared/media-player/media-storage";
import { Play } from "@/components/shared/media-player/shared";
import { useMediaVolumeCommit } from "@/components/shared/media-player/use-media-volume-commit";
import {
  configureMediaProvider,
  getMediaElementFromProvider,
  preProcessDashManifest,
  useMediaSource,
} from "@/components/shared/media-player/utils";
import { Button } from "@/components/ui/button";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useLayout } from "@/contexts/layout-context";
import { useOwnTheme } from "@/contexts/theme-context";
import { useMobile } from "@/hooks/use-mobile";
import { cn } from "@/utils/lib";
import type { AudioMetadata } from "@/utils/media-utils";
import {
  getMediaClientIdFromUrl,
  getMediaMetaFromUrl,
} from "@/utils/post-utils";
import type { PostMediaMeta } from "@/utils/types";

type PostAudioPlayerProps = Omit<
  ComponentProps<typeof MediaPlayer>,
  "children" | "muted" | "storage" | "volume"
> & {
  src: string;
  mediaMeta?: PostMediaMeta;
  onPlayChange?: (isPlaying: boolean) => void;
  postHash?: string | null;
  sourceLink?: string;
};

export function PostAudioPlayer({
  src,
  mediaMeta,
  onPlayChange,
  postHash = null,
  className,
  sourceLink,
  onProviderChange: onProviderChangeProp,
  ...props
}: PostAudioPlayerProps) {
  const {
    setPlayingElement,
    mediaVolume,
    setMediaVolume,
    mediaMuted,
    setMediaMuted,
  } = useLayout();
  const { themeColor, customColor, theme, themeRadius } = useOwnTheme();
  const { hasMouse } = useMobile();
  const playerRef = useRef<MediaPlayerInstance | null>(null);
  const storage = useMemo(
    () => new LocalMediaStorageWithoutVolume("music-player"),
    [],
  );
  const commitMediaVolume = useMediaVolumeCommit({
    mediaVolume,
    setMediaVolume,
  });

  const source = useMediaSource({
    url: src,
    preProcessManifest: preProcessDashManifest,
  });

  if (!mediaMeta) {
    throw new Error("Media meta is required");
  }

  const [mediaElement, setMediaElement] = useState<HTMLMediaElement | null>(
    null,
  );
  const metaData = getMediaMetaFromUrl(src, mediaMeta) as AudioMetadata;

  if (!metaData) {
    throw new Error("Media meta could not be found");
  }

  const title = metaData?.title || "Untitled";
  const duration = metaData?.duration;

  const handleProviderChange = useCallback<
    NonNullable<ComponentProps<typeof MediaPlayer>["onProviderChange"]>
  >(
    (provider, event) => {
      // Vidstack resolves the concrete <audio>/<video> provider after mount,
      // so the media element must be read from this lifecycle event.
      configureMediaProvider(provider);
      setMediaElement(getMediaElementFromProvider(provider));
      onProviderChangeProp?.(provider, event);
    },
    [onProviderChangeProp],
  );
  const handleUserMediaIntent = useCallback(() => {
    const nextVolume = mediaVolume > 0 ? mediaVolume : 1;

    setMediaMuted(false);
    commitMediaVolume(nextVolume, { immediate: true });
    playerRef.current?.remoteControl.unmute();
    playerRef.current?.remoteControl.changeVolume(nextVolume);
  }, [commitMediaVolume, mediaVolume, setMediaMuted]);
  const handleVolumeToggle = useCallback(() => {
    if (mediaMuted || mediaVolume <= 0) {
      handleUserMediaIntent();
      return;
    }

    setMediaMuted(true);
    playerRef.current?.remoteControl.mute();
  }, [handleUserMediaIntent, mediaMuted, mediaVolume, setMediaMuted]);
  const handleMediaVolumeChangeRequest = useCallback(
    (volume: number) => {
      if (volume > 0) {
        setMediaMuted(false);
      } else {
        setMediaMuted(true);
      }

      commitMediaVolume(volume);
    },
    [commitMediaVolume, setMediaMuted],
  );
  const effectiveMuted = mediaMuted || mediaVolume <= 0;

  return (
    <MediaPlayer
      //logLevel={process.env.NODE_ENV !== "production" ? "debug" : "error"}
      ref={playerRef}
      className={cn(
        "flex w-full cursor-default flex-col rounded-full bg-card",
        className,
      )}
      role="button"
      title={title}
      src={source as HLSSrc | DASHSrc}
      crossOrigin
      playsInline
      load="play"
      viewType="audio"
      streamType="on-demand"
      storage={storage}
      volume={mediaVolume}
      muted={effectiveMuted}
      duration={duration}
      onPlay={() => {
        setPlayingElement(mediaElement);
        onPlayChange?.(true);
      }}
      onPause={() => {
        setPlayingElement((current) =>
          current === mediaElement ? null : current,
        );
        onPlayChange?.(false);
      }}
      onEnded={() => {
        setPlayingElement((current) =>
          current === mediaElement ? null : current,
        );
        onPlayChange?.(false);
      }}
      onProviderChange={handleProviderChange}
      onMediaVolumeChangeRequest={handleMediaVolumeChangeRequest}
      {...props}
    >
      <div className="flex w-full">
        <MediaProvider />
        <div className="flex w-full items-center gap-2 py-3 pr-3 pl-2">
          <div className="relative flex items-center">
            <Play onUserMediaIntent={handleUserMediaIntent} />
            <AudioBlobVisualizer
              className="
                pointer-events-none absolute top-1/2 left-1/2 -translate-1/2
              "
              mediaElement={mediaElement}
              color={customColor || themeColor}
              glowColor={customColor || themeColor}
              size={40}
              initialRadius={themeRadius}
            />
          </div>
          <div className="flex min-w-0 grow flex-col gap-1">
            <div className="flex min-w-0 gap-4">
              <div
                className="
                  w-full grow truncate pr-2 font-sans text-sm font-medium
                "
              >
                {title}
              </div>
              {sourceLink && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Link href={sourceLink}>
                        <LuLink2
                          className="
                            cursor-pointer text-lg text-muted
                            hover:text-primary
                          "
                        />
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent>Go to Source Post</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              {postHash && (
                <div>
                  <AddToCollectionDropdown
                    types={["playlist"]}
                    createText="New Playlist"
                    addToText="Add to Playlist"
                    itemData={{
                      type: "audio",
                      postHash,
                      clientId: getMediaClientIdFromUrl(src),
                    }}
                    align="end"
                  >
                    <LuLayers
                      className="
                        cursor-pointer text-lg text-muted
                        hover:text-primary
                      "
                    />
                  </AddToCollectionDropdown>
                </div>
              )}
            </div>
            <div className="flex items-center gap-4 text-xs text-muted">
              <div
                className="
                flex items-center gap-1 font-mono text-xs text-muted
              "
              >
                <Time type="current" />
                <div>/</div>
                <Time type="duration" />
              </div>
              <div className="grow">
                <TimeSlider.Root
                  className="
                    group relative flex h-[5px] w-full cursor-pointer touch-none
                    items-center outline-hidden select-none
                    aria-hidden:hidden
                  "
                >
                  <TimeSlider.Track
                    className="
                      relative z-0 h-[5px] w-full rounded-sm bg-border
                      ring-sky-400
                      group-data-focus:ring-3
                    "
                  >
                    <TimeSlider.TrackFill
                      className="
                        absolute z-10 h-full w-(--slider-fill) rounded-sm
                        bg-primary will-change-[width]
                      "
                    />
                    <TimeSlider.Progress
                      className="
                        absolute h-full w-(--slider-progress) rounded-sm
                        bg-white/5 will-change-[width]
                      "
                    />
                  </TimeSlider.Track>
                  <TimeSlider.Thumb
                    className="
                      absolute top-1/2 left-(--slider-fill) z-20 size-[15px]
                      -translate-1/2 rounded-full border border-border
                      bg-primary opacity-0 ring-white/40 transition-opacity
                      will-change-[left]
                      group-data-active:opacity-100
                      group-data-dragging:ring-4
                    "
                  />
                </TimeSlider.Root>
              </div>
              {/* On device volume is controlled by the OS */}
              {hasMouse && (
                <div className={cn(theme?.includes("modern") && "pr-2")}>
                  <HoverCard>
                    <HoverCardTrigger closeDelay={0} delay={0}>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="flex size-auto flex-col p-0 text-lg"
                        onClick={handleVolumeToggle}
                      >
                        {effectiveMuted && <LuVolumeX />}
                        {!effectiveMuted && mediaVolume > 0.7 && <LuVolume2 />}
                        {!effectiveMuted &&
                          mediaVolume > 0.2 &&
                          mediaVolume <= 0.7 && <LuVolume1 />}
                        {!effectiveMuted && mediaVolume <= 0.2 && <LuVolume />}
                      </Button>
                    </HoverCardTrigger>
                    <HoverCardContent
                      className="block h-[80px] w-auto min-w-0"
                      align="center"
                      side="top"
                      sideOffset={0}
                    >
                      <VolumeSlider.Root
                        className="
                          group relative flex h-full max-h-[100px] w-[5px]
                          cursor-pointer touch-none items-center outline-hidden
                          select-none
                          aria-hidden:hidden
                        "
                        orientation="vertical"
                      >
                        <VolumeSlider.Track
                          className="
                            relative z-0 h-full w-[5px] rounded-sm bg-border
                            ring-sky-400
                            group-data-focus:ring-3
                          "
                        >
                          <VolumeSlider.TrackFill
                            className="
                              absolute bottom-0 h-(--slider-fill) w-full
                              rounded-sm bg-primary will-change-[height]
                            "
                          />
                        </VolumeSlider.Track>
                        <VolumeSlider.Thumb
                          className="
                            absolute bottom-(--slider-fill) left-1/2 z-20
                            size-[15px] -translate-x-1/2 translate-y-1/2
                            rounded-full border border-border bg-primary
                            ring-white/40 transition-opacity
                            will-change-[bottom]
                            group-data-dragging:ring-4
                          "
                        />
                      </VolumeSlider.Root>
                    </HoverCardContent>
                  </HoverCard>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </MediaPlayer>
  );
}
